import { MonitoringEventInput } from "./api";

const BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;
const SESSION_KEY = "evt_session_id";
let fallbackSessionId: string | null = null;

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getSessionId(): string {
  try {
    if (typeof sessionStorage !== "undefined") {
      const storedSessionId = sessionStorage.getItem(SESSION_KEY);
      if (storedSessionId) return storedSessionId;

      const newSessionId = generateId();
      sessionStorage.setItem(SESSION_KEY, newSessionId);
      return newSessionId;
    }
  } catch {
    // Storage can be blocked by browser privacy settings. Keep tracking usable
    // with a process-local session id instead of failing during module import.
  }

  if (!fallbackSessionId) {
    fallbackSessionId = generateId();
  }
  return fallbackSessionId;
}

class EventTracker {
  private queue: MonitoringEventInput[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private userId: string | null = null;
  private tenantId: string | null = null;
  private sessionId: string = getSessionId();
  private ingestFn: ((events: MonitoringEventInput[]) => Promise<unknown>) | null = null;

  init(
    ingestFn: (events: MonitoringEventInput[]) => Promise<unknown>,
    userId: string | null,
    tenantId: string | null
  ) {
    this.ingestFn = ingestFn;
    this.userId = userId;
    this.tenantId = tenantId;

    if (!this.flushTimer) {
      this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
    }

    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  }

  updateContext(userId: string | null, tenantId: string | null) {
    this.userId = userId;
    this.tenantId = tenantId;
  }

  track(
    eventType: string,
    payload: Record<string, unknown> = {},
    options: { severity?: string; category?: string } = {}
  ) {
    // Skip all tracking when on the event monitor page to avoid noise
    if (window.location.pathname === "/event-monitor") return;

    const event: MonitoringEventInput = {
      event_type: eventType,
      category: options.category || "user_action",
      severity: options.severity || "info",
      payload,
      user_id: this.userId || undefined,
      tenant_id: this.tenantId || undefined,
      session_id: this.sessionId,
      page_url: window.location.href,
      created_at: new Date().toISOString(),
    };
    this.queue.push(event);
    if (this.queue.length >= BATCH_SIZE) {
      this.flush();
    }
  }

  trackError(error: Error, context: Record<string, unknown> = {}) {
    this.track(
      "error",
      { message: error.message, stack: error.stack, ...context },
      { severity: "error", category: "error" }
    );
    this.flush();
  }

  private async flush() {
    if (this.queue.length === 0 || !this.ingestFn) return;
    const batch = this.queue.splice(0, this.queue.length);
    try {
      await this.ingestFn(batch);
    } catch {
      // Re-queue on failure (once, cap at 50 to avoid unbounded growth)
      this.queue.unshift(...batch.slice(0, 50));
    }
  }

  private flushBeacon() {
    // /api/events requires Clerk auth and sendBeacon cannot attach Authorization.
    // Drop pending unload telemetry instead of sending guaranteed 401 requests.
    this.queue.splice(0);
  }

  private handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      this.flushBeacon();
    }
  };

  private handleBeforeUnload = () => {
    this.flushBeacon();
  };

  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  }
}

export const eventTracker = new EventTracker();
